/**
 * @file Redux mappers for composeDraft container
 */

import { MapDispatchToProps, MapStateToProps } from 'react-redux';

import AppState from '../../../models/state';

import { clearDraftRequest } from '../../../store/actions/draftsMain';
import { getIsDefaultDraftChanged } from '../helpers';

import { DispatchProps, OwnProps, StateProps } from './types';

/**
 * Map Redux dispatch to compose props
 *
 * @param dispatch          Redux dispatch
 * @param ownProps          Props passed to the container
 * @param ownProps.nodeType The key for the Redux store draft
 * @returns                 Props to add to compose
 */
export const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (
  dispatch,
  { nodeType },
) => ({
  /**
   * Dispatches clearing draft request
   *
   * @returns void
   */
  requestDraftClear: () => dispatch(clearDraftRequest(nodeType)),
});

/**
 * Map Redux app state to component props
 *
 * @param appState                   Current app state
 * @param appState.drafts            Drafts in the state
 * @param appState.draftsAttachments Attachments drafts in the state
 * @param ownProps                   Passed props
 * @param ownProps.nodeType          The id of the draft
 * @returns                          Mapped state
 */
export const mapStateToProps: MapStateToProps<
  StateProps,
  OwnProps,
  AppState
> = ({ drafts, draftsAttachments }, { nodeType }) => {
  const draft = drafts.get(nodeType);
  const attachmentsDraft = draftsAttachments.get(nodeType);

  if (draft === undefined || attachmentsDraft === undefined) {
    return {
      isDraftChanged: false,
      topicId: null,
    };
  }

  return {
    isDraftChanged: getIsDefaultDraftChanged(draft, attachmentsDraft),
    topicId: draft.templateTopicSaved,
  };
};
